import React, { useEffect, useMemo, useState } from "react";

// Accepts optional parsed JSON `data`; if not provided, it will fetch using URL params
function FolderMode({ data: externalData }) {
  const [level, setLevel] = useState(1);
  const [selectedItemIdx, setSelectedItemIdx] = useState(null);
  const [selectedSideIdx, setSelectedSideIdx] = useState(null);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [imageModalOpen, setImageModalOpen] = useState(false);
  const [imageModalIndex, setImageModalIndex] = useState(0);
  const [downloadingAll, setDownloadingAll] = useState(false);

  // Prefer external data if provided; otherwise fetch from server using vid/order_no
  useEffect(() => {
    if (externalData) { setData(externalData); return; }
    try {
      const params = new URLSearchParams(window.location.search);
      const vid = params.get('vid') || '';
      const order_no = params.get('order_no') || params.get('orderNo') || '';
      if (!vid || !order_no) return; // allow rendering without data
      setLoading(true);
      const url = `https://weimobcstraiton-fcvqzwicwr.cn-hongkong.fcapp.run/order/preview?vid=${encodeURIComponent(vid)}&order_no=${encodeURIComponent(order_no)}`;
      fetch(url, { method: 'GET', headers: { 'Accept': 'application/json' } })
        .then(async (res) => {
          const text = await res.text();
          try {
            return { ok: res.ok, status: res.status, data: JSON.parse(text) };
          } catch {
            return { ok: res.ok, status: res.status, data: text };
          }
        })
        .then((result) => {
          if (!result.ok) {
            setError(`Request failed with status ${result.status}`);
          }
          setData(result.data);
        })
        .catch((err) => {
          setError(err?.message || 'Request failed');
        })
        .finally(() => setLoading(false));
    } catch (e) {
      console.warn('Failed to initialize preview fetch:', e);
    }
  }, [externalData]);

  const transformToPreview = (url) => {
    if (!url) return url;
    try {
      return String(url).replace(/([^\\/]+)$/i, (seg) => seg.replace(/design/gi, 'preview'));
    } catch { return url; }
  };

  // Resolve order ID for folder naming: URL param -> payload fields
  const getOrderId = () => {
    try {
      const params = new URLSearchParams(window.location.search);
      const fromUrl = params.get('order_no') || params.get('orderNo');
      if (fromUrl) return String(fromUrl);
    } catch { /* ignore */ }
    const src = externalData ?? data;
    const qp = src?.qp_payload || {};
    return qp.thirdOrderNumber || qp.thirdOrderId || 'Order';
  };

  const getSafeFolderName = (name) => String(name || '')
    .trim()
    .replace(/[\s/\\:<>"'|?*]+/g, '-')
    .slice(0, 120) || 'Order';

  // --- Utilities for dynamic ZIP dependencies (JSZip + FileSaver) ---
  const ensureZipDeps = async () => {
    // If already present, reuse
    const hasJSZip = typeof window !== 'undefined' && window.JSZip;
    const hasFileSaver = typeof window !== 'undefined' && window.saveAs;
    if (hasJSZip && hasFileSaver) return { JSZip: window.JSZip, saveAs: window.saveAs };

    // Helper to load a script
    const loadScript = (src) => new Promise((resolve, reject) => {
      const existing = document.querySelector(`script[src="${src}"]`);
      if (existing) {
        existing.addEventListener('load', () => resolve());
        existing.addEventListener('error', (e) => reject(e));
        return;
      }
      const s = document.createElement('script');
      s.src = src;
      s.async = true;
      s.onload = () => resolve();
      s.onerror = (e) => reject(e);
      document.head.appendChild(s);
    });

    // Use reliable CDN builds
    // JSZip UMD exposes window.JSZip; FileSaver exposes window.saveAs
    await loadScript('https://cdn.jsdelivr.net/npm/jszip@3.10.1/dist/jszip.min.js');
    await loadScript('https://cdn.jsdelivr.net/npm/file-saver@2.0.5/dist/FileSaver.min.js');

    if (!window.JSZip || !window.saveAs) throw new Error('Failed to load ZIP dependencies');
    return { JSZip: window.JSZip, saveAs: window.saveAs };
  };

  // Collect all printing and preview images from the dataset
  const collectAllImages = () => {
    const src = externalData ?? data;
    const all = [];
    if (!src) return all;
    const rawItems = src?.qp_payload?.items || src?.items || [];
    const itemsArr = Array.isArray(rawItems) ? rawItems : [];
    for (let i = 0; i < itemsArr.length; i++) {
      const item = itemsArr[i];
      const designs = Array.isArray(item?.customizeProject?.designs) ? item.customizeProject.designs : [];
      for (let d = 0; d < designs.length; d++) {
        const design = designs[d];
        const sideName = design?.side ?? `Side_${d + 1}`;
        const pcs = Array.isArray(design?.pageContentDesigns) ? design.pageContentDesigns : [];
        for (let p = 0; p < pcs.length; p++) {
          const imgObj = pcs[p];
          const printUrl = imgObj?.image;
          const prevUrl = transformToPreview(printUrl);
          const pageNum = (imgObj?.pageContentIndex ?? p) + 1;
          // Derive extension from the URL (e.g., .png/.jpg), default to .png
          const getExt = (u) => {
            if (!u || typeof u !== 'string') return 'png';
            const m = u.match(/\.([a-zA-Z0-9]+)(?:\?|#|$)/);
            if (m && m[1]) return m[1].toLowerCase();
            return 'png';
          };
          const ext = getExt(printUrl || prevUrl);
          const safeSide = String(sideName).trim().replace(/[\s/\\]+/g, '-');
          const itemFolder = `Item${i + 1}`;
          const sideFolder = safeSide || `Side_${d + 1}`;
          const fileName = `Page${pageNum}.${ext}`;
          if (printUrl) {
            all.push({ type: 'Printing', url: printUrl, name: fileName, itemFolder, sideFolder });
          }
          if (prevUrl) {
            all.push({ type: 'Preview', url: prevUrl, name: fileName, itemFolder, sideFolder });
          }
        }
      }
    }
    return all;
  };

  const downloadAllAsZip = async () => {
    try {
      setDownloadingAll(true);
      const assets = collectAllImages();
      if (!assets.length) {
        alert('No images to download.');
        return;
      }

      const { JSZip, saveAs } = await ensureZipDeps();
      const zip = new JSZip();
      const orderFolderName = getSafeFolderName(getOrderId());
      const root = zip.folder(orderFolderName);

      // Fetch as blobs
      const results = await Promise.allSettled(
        assets.map(async (asset) => {
          const resp = await fetch(asset.url, { mode: 'cors' });
          if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
          const blob = await resp.blob();
          return { asset, blob };
        })
      );

      let successCount = 0;
      results.forEach((res) => {
        if (res.status === 'fulfilled') {
          const { asset, blob } = res.value;
          const folderPath = `${asset.itemFolder}/${asset.sideFolder}/${asset.type}`;
          const folder = root.folder(folderPath);
          folder.file(asset.name, blob);
          successCount++;
        } else {
          console.warn('Failed to fetch asset:', res.reason);
        }
      });

      if (successCount === 0) {
        alert('Failed to fetch all images.');
        return;
      }

      const zipName = `${orderFolderName}.zip`;

      const blob = await zip.generateAsync({ type: 'blob' });
      saveAs(blob, zipName);
    } catch (e) {
      console.error('Download all failed:', e);
      alert(`Download failed: ${e?.message || e}`);
    } finally {
      setDownloadingAll(false);
    }
  };

  // Modal close on ESC
  useEffect(() => {
    if (!imageModalOpen) return;
    const onKey = (e) => {
      if (e.key === 'Escape') setImageModalOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [imageModalOpen]);

  // Helper: Items structure (robust to data shape)
  const items = useMemo(() => {
    const src = externalData ?? data;
    if (!src) return [];
    const rawItems = src?.qp_payload?.items || src?.items || [];
    return Array.isArray(rawItems) ? rawItems : [];
  }, [externalData, data]);

  // Loading / error basics
  if (!externalData && loading) {
    return <div style={{ padding: 24 }}>Loading…</div>;
  }
  if (!externalData && error) {
    return <div style={{ padding: 24, color: 'red' }}>{String(error)}</div>;
  }

  // Level 1: Show each order item's first thumbnail
  if (level === 1) {
    return (
      <div style={{ padding: 24 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <div style={{ fontSize: 18, fontWeight: 700 }}>Order Items</div>
          <button
            onClick={downloadAllAsZip}
            disabled={downloadingAll || items.length === 0}
            style={{
              padding: '8px 14px',
              borderRadius: 8,
              border: '1px solid #dcdcdc',
              background: downloadingAll ? '#f3f3f3' : '#ffffff',
              boxShadow: '0 1px 2px rgba(0,0,0,0.06)',
              cursor: downloadingAll ? 'not-allowed' : 'pointer',
              fontWeight: 600
            }}
            title="Download all preview and printing images as ZIP"
          >
            {downloadingAll ? 'Preparing…' : 'Download All'}
          </button>
        </div>
        <div style={{ display: "flex", gap: 24, flexWrap: 'wrap' }}>
          {items.map((item, idx) => (
            <button
              key={idx}
              onClick={() => {
                setSelectedItemIdx(idx);
                setLevel(2);
              }}
              style={{
                border: "1px solid #eee",
                padding: 12,
                borderRadius: 8,
                background: "#fafaff",
                cursor: "pointer",
                textAlign: "center"
              }}
            >
              <img
                src={transformToPreview(item?.customizeProject?.comparisonThumbnail)}
                alt={`Order Item ${idx + 1}`}
                style={{ width: 140, height: 140, objectFit: "cover", borderRadius: 6 }}
              />
              <div style={{ marginTop: 8, fontWeight: "bold" }}>Item {idx + 1}</div>
            </button>
          ))}
        </div>
      </div>
    );
  }

  // Level 2: Show sub-order items (design sides) for selected item
  const selectedItem = items[selectedItemIdx];
  const designs = Array.isArray(selectedItem?.customizeProject?.designs) ? selectedItem.customizeProject.designs : [];
  if (level === 2) {
    return (
      <div style={{ padding: 24 }}>
        <button
          onClick={() => setLevel(1)}
          style={{
            marginBottom: 16,
            padding: '8px 12px',
            borderRadius: 8,
            border: '1px solid #dcdcdc',
            background: '#ffffff',
            boxShadow: '0 1px 2px rgba(0,0,0,0.06)',
            cursor: 'pointer',
            display: 'inline-flex',
            alignItems: 'center',
            gap: 8,
            fontWeight: 600
          }}
        >
          <span style={{ fontSize: 16 }}>←</span>
          <span>Back</span>
        </button>
        <div style={{ display: "flex", gap: 24, flexWrap: 'wrap' }}>
          {designs.map((design, idx) => (
            <button
              key={idx}
              onClick={() => {
                setSelectedSideIdx(idx);
                setLevel(3);
              }}
              style={{
                border: "1px solid #eee",
                padding: 12,
                borderRadius: 8,
                background: "#fafaff",
                cursor: "pointer",
                textAlign: "center"
              }}
            >
              <img
                src={transformToPreview(Array.isArray(design?.pageContentDesigns) && design.pageContentDesigns[0]?.image)}
                alt={`Side ${design.side}`}
                style={{ width: 140, height: 140, objectFit: "cover", borderRadius: 6 }}
              />
              <div style={{ marginTop: 8, fontWeight: "bold" }}>{design.side}</div>
            </button>
          ))}
        </div>
      </div>
    );
  }

  // Level 3: Show all images for selected side
  const selectedDesign = designs?.[selectedSideIdx];
  const pageContentDesigns = Array.isArray(selectedDesign?.pageContentDesigns) ? selectedDesign.pageContentDesigns : [];
  if (level === 3) {
    return (
      <div style={{ padding: 24 }}>
        <button
          onClick={() => setLevel(2)}
          style={{
            marginBottom: 16,
            padding: '8px 12px',
            borderRadius: 8,
            border: '1px solid #dcdcdc',
            background: '#ffffff',
            boxShadow: '0 1px 2px rgba(0,0,0,0.06)',
            cursor: 'pointer',
            display: 'inline-flex',
            alignItems: 'center',
            gap: 8,
            fontWeight: 600
          }}
        >
          <span style={{ fontSize: 16 }}>←</span>
          <span>Back</span>
        </button>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 20 }}>
          {pageContentDesigns.map((imgObj, idx) => (
            <div key={idx} style={{
              border: "1px solid #eaeaea",
              padding: 8,
              borderRadius: 8,
              textAlign: "center"
            }}>
              <img
                src={transformToPreview(imgObj?.image)}
                alt={`Page ${imgObj.pageContentIndex + 1}`}
                style={{ width: 140, height: 180, objectFit: "contain", borderRadius: 6, background: "#fff", cursor: 'zoom-in' }}
                onClick={() => { setImageModalIndex(idx); setImageModalOpen(true); }}
              />
              <div style={{marginTop:8, fontSize:12}}>Page {imgObj.pageContentIndex + 1}</div>
              {imgObj.effect && (
                <div style={{fontSize:12, color:"#555"}}>Effect: {imgObj.effect}</div>
              )}
            </div>
          ))}
        </div>
        {imageModalOpen && (
          <div
            onClick={() => setImageModalOpen(false)}
            style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}
          >
            <div
              onClick={(e) => e.stopPropagation()}
              style={{ background: '#111', padding: 12, borderRadius: 10, maxWidth: '95vw', maxHeight: '95vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
            >
              <img
                src={transformToPreview(pageContentDesigns[imageModalIndex]?.image)}
                alt={`Page ${pageContentDesigns[imageModalIndex]?.pageContentIndex + 1}`}
                style={{ maxWidth: '92vw', maxHeight: '92vh', objectFit: 'contain', borderRadius: 8, background: '#fff' }}
              />
              <button
                onClick={() => setImageModalOpen(false)}
                style={{ position: 'absolute', top: 16, right: 16, padding: '6px 10px', borderRadius: 8, border: '1px solid #ddd', background: '#fff', cursor: 'pointer', fontWeight: 600 }}
              >
                Close
              </button>
            </div>
          </div>
        )}
      </div>
    );
  }

  return null;
}

export default FolderMode;
